<script setup>
/* global axios */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import {
  buildCrmContextMessage,
  buildCrmOpenOpportunityMessage,
} from '../../../components/crm/crm-conversation-protocol';
import { consumePendingCrmOpportunity } from '../../../components/crm/crm-navigation-state';
import {
  buildCrmSessionMessage,
  crmWorkspaceUrl,
  isTrustedCrmReadyMessage,
  normalizeAccountId,
  normalizeCrmSession,
  validateHubOrigin,
} from './crm-host-protocol';

const props = defineProps({
  conversationId: {
    type: [String, Number],
    default: null,
  },
  contactId: {
    type: [String, Number],
    default: null,
  },
  agentId: {
    type: [String, Number],
    default: null,
  },
});
const route = useRoute();
const { t } = useI18n();
const crmFrame = ref(null);
const hubOrigin = ref('');
const iframeSrc = ref('');
const status = ref('loading');
const frameReady = ref(false);
const sessionRequest = ref(null);
const sessionBlocked = ref(false);
const errorMessage = ref('');
const isMounted = ref(false);
const accountId = computed(() => normalizeAccountId(route.params.accountId));
const conversationContext = computed(() => {
  const currentAccountId = accountId.value;
  const currentConversationId = normalizeAccountId(props.conversationId);
  if (!currentAccountId || !currentConversationId) return null;
  return buildCrmContextMessage(
    {
      accountId: currentAccountId,
      conversationId: currentConversationId,
      contactId: props.contactId,
      agentId: props.agentId,
    },
    currentAccountId
  );
});
const REQUEST_TIMEOUT_MS = 15_000;
const HANDSHAKE_TIMEOUT_MS = 15_000;
const SESSION_REQUEST_COOLDOWN_MS = 1_000;
let generation = 0;
let requestController = null;
let handshakeTimer = null;
let lastSessionRequestAt = null;
let pendingOpportunityId = null;

const title = computed(() => t('SIDEBAR.CRM', 'Deals'));
const statusMessage = computed(() => {
  if (status.value === 'disabled') {
    return t(
      'CRM_WORKSPACE.DISABLED',
      'Deals are not enabled for this account.'
    );
  }
  if (status.value === 'error') {
    return (
      errorMessage.value ||
      t(
        'CRM_WORKSPACE.ERROR',
        'The Deals workspace is temporarily unavailable.'
      )
    );
  }
  if (status.value === 'ready')
    return t('CRM_WORKSPACE.READY', 'Deals workspace ready.');
  return t('CRM_WORKSPACE.LOADING', 'Loading Deals workspace…');
});

function isCurrent(run, expectedAccountId) {
  return (
    isMounted.value &&
    generation === run &&
    accountId.value === expectedAccountId
  );
}

function isAbortError(error) {
  return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}

function httpStatus(error) {
  return error?.response?.status;
}

function clearHandshakeTimer() {
  if (handshakeTimer === null) return;
  clearTimeout(handshakeTimer);
  handshakeTimer = null;
}

function clearFrameSession() {
  const frame = crmFrame.value;
  if (!frame?.contentWindow || !hubOrigin.value) return;

  // Hub revokes the in-memory bearer on this message. The parent cannot issue
  // a cross-origin DELETE; a copied bearer can remain valid until its TTL.
  frame.contentWindow.postMessage({ type: 'crm:clear' }, hubOrigin.value);
}

function resetAccountState() {
  clearFrameSession();
  clearHandshakeTimer();
  hubOrigin.value = '';
  iframeSrc.value = '';
  frameReady.value = false;
  sessionRequest.value = null;
  sessionBlocked.value = false;
  errorMessage.value = '';
  lastSessionRequestAt = null;
}

function scheduleHandshakeTimeout(run, expectedAccountId) {
  clearHandshakeTimer();
  handshakeTimer = setTimeout(() => {
    handshakeTimer = null;
    if (!isCurrent(run, expectedAccountId) || frameReady.value) return;
    clearFrameSession();
    sessionBlocked.value = true;
    status.value = 'error';
    errorMessage.value = t(
      'CRM_WORKSPACE.TIMEOUT',
      'The Deals workspace did not respond. Try again.'
    );
  }, HANDSHAKE_TIMEOUT_MS);
}

async function loadAccount(nextAccountId) {
  generation += 1;
  const run = generation;
  requestController?.abort();
  resetAccountState();
  status.value = 'loading';

  if (!nextAccountId) {
    status.value = 'error';
    errorMessage.value = t(
      'CRM_WORKSPACE.INVALID_ACCOUNT',
      'This account cannot open the Deals workspace.'
    );
    return;
  }

  const controller = new AbortController();
  requestController = controller;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${nextAccountId}/crm/config`,
      {
        signal: controller.signal,
        timeout: REQUEST_TIMEOUT_MS,
      }
    );
    if (!isCurrent(run, nextAccountId)) return;

    const origin = validateHubOrigin(response?.data?.hubOrigin);
    const url = crmWorkspaceUrl(origin);
    if (!origin || !url) throw new Error('invalid CRM origin');

    hubOrigin.value = origin;
    iframeSrc.value = url;
    scheduleHandshakeTimeout(run, nextAccountId);
  } catch (error) {
    if (!isCurrent(run, nextAccountId) || isAbortError(error)) return;
    status.value = httpStatus(error) === 404 ? 'disabled' : 'error';
    errorMessage.value =
      status.value === 'disabled'
        ? t('CRM_WORKSPACE.DISABLED', 'Deals are not enabled for this account.')
        : t(
            'CRM_WORKSPACE.ERROR',
            'The Deals workspace is temporarily unavailable.'
          );
  }
}

function sendSession(nextSession, expectedAccountId, expectedOrigin) {
  const frame = crmFrame.value;
  const message = buildCrmSessionMessage(
    nextSession,
    expectedAccountId,
    expectedOrigin
  );
  if (!frame?.contentWindow || !message) return false;

  frame.contentWindow.postMessage(message, expectedOrigin);
  if (conversationContext.value) {
    frame.contentWindow.postMessage(conversationContext.value, expectedOrigin);
  }
  const openOpportunity = buildCrmOpenOpportunityMessage(
    expectedAccountId,
    pendingOpportunityId
  );
  if (openOpportunity) {
    frame.contentWindow.postMessage(openOpportunity, expectedOrigin);
    pendingOpportunityId = null;
  }
  status.value = 'ready';
  return true;
}

function requestSession() {
  const expectedAccountId = accountId.value;
  const expectedOrigin = hubOrigin.value;
  const run = generation;
  if (!expectedAccountId || !expectedOrigin || sessionBlocked.value) return;
  if (sessionRequest.value) return;
  const now = Date.now();
  if (
    lastSessionRequestAt !== null &&
    now - lastSessionRequestAt < SESSION_REQUEST_COOLDOWN_MS
  ) {
    return;
  }
  lastSessionRequestAt = now;

  const request = axios
    .post(`/api/v1/accounts/${expectedAccountId}/crm/session`, undefined, {
      signal: requestController?.signal,
      timeout: REQUEST_TIMEOUT_MS,
    })
    .then(response => {
      if (!isCurrent(run, expectedAccountId)) return;
      const nextSession = normalizeCrmSession(
        response?.data,
        expectedAccountId,
        expectedOrigin
      );
      if (!nextSession) throw new Error('invalid CRM session');
      sendSession(nextSession, expectedAccountId, expectedOrigin);
    })
    .catch(error => {
      if (!isCurrent(run, expectedAccountId) || isAbortError(error)) return;
      sessionBlocked.value = true;
      status.value = 'error';
      errorMessage.value = t(
        'CRM_WORKSPACE.ERROR',
        'The Deals workspace is temporarily unavailable.'
      );
    })
    .finally(() => {
      if (sessionRequest.value === request) sessionRequest.value = null;
    });
  sessionRequest.value = request;
}

function onMessage(event) {
  const frame = crmFrame.value;
  if (
    !frame ||
    !isTrustedCrmReadyMessage(event, hubOrigin.value, frame.contentWindow)
  ) {
    return;
  }

  clearHandshakeTimer();
  frameReady.value = true;
  requestSession();
}

function retry() {
  loadAccount(accountId.value);
}

watch(accountId, nextAccountId => {
  if (isMounted.value) loadAccount(nextAccountId);
});

watch(conversationContext, nextContext => {
  const frame = crmFrame.value;
  if (
    !frameReady.value ||
    !frame?.contentWindow ||
    !hubOrigin.value ||
    !nextContext
  ) {
    return;
  }
  frame.contentWindow.postMessage(nextContext, hubOrigin.value);
});

onMounted(() => {
  isMounted.value = true;
  pendingOpportunityId = consumePendingCrmOpportunity(accountId.value);
  // Install the listener before config resolves and before the iframe is mounted.
  window.addEventListener('message', onMessage);
  loadAccount(accountId.value);
});

onBeforeUnmount(() => {
  isMounted.value = false;
  generation += 1;
  requestController?.abort();
  clearHandshakeTimer();
  clearFrameSession();
  window.removeEventListener('message', onMessage);
});
</script>

<template>
  <main
    class="flex flex-col flex-1 min-h-0 bg-n-background"
    data-testid="crm-workspace"
  >
    <header
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <h1 class="text-base font-semibold text-n-slate-12">{{ title }}</h1>
      <button
        v-if="status === 'error'"
        type="button"
        class="px-3 py-1.5 rounded-lg outline outline-1 outline-n-weak text-n-slate-12 hover:bg-n-alpha-2"
        @click="retry"
      >
        {{ t('CRM_WORKSPACE.RETRY', 'Retry') }}
      </button>
    </header>

    <p
      v-if="status === 'disabled'"
      class="p-6 text-sm text-n-slate-11"
      role="status"
    >
      {{ statusMessage }}
    </p>
    <p
      v-else-if="status === 'error'"
      class="p-6 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ statusMessage }}
    </p>
    <section v-else class="relative flex flex-col flex-1 min-h-0">
      <p
        v-if="!frameReady"
        class="absolute inset-0 z-10 grid place-items-center text-sm text-n-slate-11 bg-n-background"
        aria-live="polite"
      >
        {{ statusMessage }}
      </p>
      <iframe
        v-if="iframeSrc"
        ref="crmFrame"
        :key="accountId"
        :src="iframeSrc"
        :title="title"
        class="w-full h-full min-h-0 border-0"
      />
    </section>
  </main>
</template>
